import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError, formatIndian, formatWeight } from "@/lib/utils";
import { Search, Sparkles, Trash2, AlertCircle, Plus, Upload as UploadIcon, Pencil, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import CatalogProductFormDialog from "@/components/CatalogProductFormDialog";
import CatalogFilterBar, { DEFAULT_ADMIN_FILTERS, applyAdminFiltersAndSort, adminBounds } from "@/components/CatalogFilterBar";
import { AshthLiteBadge } from "@/components/AshthLiteBadge";

const STATIC_CATEGORIES = ["Ring", "Earring", "Necklace", "Pendant", "Nosepin",
                            "Bracelet", "Bangle", "Chain", "Set"];
// CS 21.54 -- "Bracelet/Bangle" is a display alias that folds two underlying
// server-side categories into one filter. When the operator picks it we do
// NOT send `category` to the server; we fetch everything else that matches
// the remaining filters and then client-side keep rows whose category is
// either "Bracelet" or "Bangle" (case-insensitive).
const COMBINED_CATEGORY_MAP = {
  "Bracelet/Bangle": ["bracelet", "bangle"],
};

// CS 21.55 -- Merge live-DB categories with the static baseline so newly
// seen categories appear without a code deploy, while keeping the curated
// order/naming for known ones. Bracelet + Bangle collapse into the
// virtual "Bracelet/Bangle" option so operators don't see both.
function buildCategoryOptions(liveCategories) {
  const merged = new Set(STATIC_CATEGORIES);
  for (const c of liveCategories || []) merged.add(c);
  // Collapse the two real categories into the display alias.
  merged.delete("Bracelet");
  merged.delete("Bangle");
  merged.add("Bracelet/Bangle");
  // Preserve the curated order for known items; append unknown ones alphabetically.
  const knownOrder = ["Ring", "Earring", "Necklace", "Pendant", "Nosepin",
                       "Bracelet/Bangle", "Chain", "Set"];
  const known = knownOrder.filter((k) => merged.has(k));
  const extra = [...merged]
    .filter((k) => !knownOrder.includes(k))
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  return [...known, ...extra];
}
const STATUS_TONE = {
  NONE: "bg-slate-100 text-slate-600 border-slate-300",
  MEMO: "bg-emerald-50 text-emerald-700 border-emerald-300",
  SOLD: "bg-slate-300 text-slate-700 border-slate-400",
};

// Admin page — Distribution Catalog. CS 20: reads from catalog_products
// (planned designs keyed by Product Code), not from the produced inventory.
export default function DistributionCatalog() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);    // product_code currently in form
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_ADMIN_FILTERS);
  // CS 21.55 -- Live category list. Fetched once on mount and refreshed
  // on demand (after edits/deletes that could shift the distinct set).
  // Merged with the static baseline via `buildCategoryOptions`.
  const [liveCategories, setLiveCategories] = useState([]);
  const categoryOptions = useMemo(
    () => buildCategoryOptions(liveCategories),
    [liveCategories],
  );

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/catalog-products/meta/categories");
      setLiveCategories(data.categories || []);
    } catch { /* non-fatal -- static baseline remains */ }
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const load = useCallback(async () => {
    setItems(null);
    const params = {};
    if (search) params.search = search;
    // CS 21.54 -- combined-category filter (e.g. "Bracelet/Bangle") is
    // applied client-side; the server accepts single values only.
    if (category && !COMBINED_CATEGORY_MAP[category]) params.category = category;
    if (statusFilter) params.status = statusFilter;
    try {
      const { data } = await api.get("/catalog-products", { params });
      setItems(data.items || []);
      setSelected(new Set());
      // CS 21.55 -- refresh the live category list after each list load
      // so a newly-created / newly-deleted category propagates without
      // a manual page refresh.
      loadCategories();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load catalog");
      setItems([]);
    }
  }, [search, category, statusFilter, loadCategories]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const toggle = (pc) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(pc)) n.delete(pc); else n.add(pc);
      return n;
    });
  };

  const selectAll = () => {
    // CS 21.54 -- filter-aware Select All. Operates ONLY on the currently
    // visible (post-filter, post-search) list, never the raw items[]. A
    // second click clears the selection back to zero.
    if (!visible || visible.length === 0) return;
    const visibleCodes = visible.map((p) => p.product_code);
    const allPicked = visibleCodes.every((c) => selected.has(c));
    setSelected(allPicked ? new Set() : new Set(visibleCodes));
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    // CS 21.54 -- explicit "cannot be undone" wording per spec.
    if (!window.confirm(
      `Delete ${selected.size} product(s) from the distribution collection?\n\n` +
      `This cannot be undone. The source catalogue product is not affected.`
    )) return;
    setBusy(true);
    try {
      const codes = Array.from(selected);
      let ok = 0;
      for (const c of codes) {
        try { await api.delete(`/catalog-products/${encodeURIComponent(c)}`); ok++; } catch { /* per-row failure already surfaced server-side; continue */ }
      }
      toast.success(`Deleted ${ok} product(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (pc) => {
    // CS 21.54 -- same "cannot be undone" wording as the bulk path.
    if (!window.confirm(
      `Delete ${pc} from the distribution collection?\n\n` +
      `This cannot be undone. The source catalogue product is not affected.`
    )) return;
    try {
      await api.delete(`/catalog-products/${encodeURIComponent(pc)}`);
      toast.success(`${pc} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const cycleStatus = async (pc, current) => {
    const next = current === "NONE" ? "MEMO" : current === "MEMO" ? "SOLD" : "NONE";
    try {
      await api.patch(`/catalog-products/${encodeURIComponent(pc)}/status`, { status: next });
      toast.success(`${pc} status → ${next}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const count = items?.length ?? 0;
  // CS 20.3 — Filter & Sort applied client-side on top of server-filtered list.
  // Selection state is keyed by product_code and intentionally NOT cleared
  // when filters change so users can flip filters without losing picks.
  // CS 21.54 -- Combined "Bracelet/Bangle" category filter is applied here
  // (server sees no category param for a combined value; we do the OR match
  // against the underlying real category strings client-side).
  const visible = useMemo(() => {
    let src = items;
    const combined = category && COMBINED_CATEGORY_MAP[category];
    if (src && combined) {
      const allow = new Set(combined.map((s) => s.toLowerCase()));
      src = src.filter((p) => allow.has(String(p.category || "").trim().toLowerCase()));
    }
    return applyAdminFiltersAndSort(src, filters);
  }, [items, filters, category]);
  const bounds = useMemo(() => adminBounds(items), [items]);
  const visibleCount = visible?.length ?? 0;

  return (
    <div className="space-y-6" data-testid="distribution-catalog-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A84C] mb-1">Distribution Module</p>
          <h1 className="font-serif text-4xl text-[#0D1B2A]">Distribution Catalog</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Planned catalog designs keyed by <span className="font-medium">Product Code</span>. Upload in bulk via the Catalog Upload page, or add one at a time below.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/distribution/catalog-upload">
            <Button variant="outline" className="border-[#b8893b] text-[#b8893b] hover:bg-[#b8893b]/5" data-testid="nav-to-catalog-upload">
              <UploadIcon className="w-4 h-4 mr-1.5" /> Bulk upload
            </Button>
          </Link>
          {canEdit && (
            <Button onClick={() => setShowCreate(true)} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="dist-catalog-create">
              <Plus className="w-4 h-4 mr-1.5" /> Add product
            </Button>
          )}
        </div>
      </header>

      <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="dist-catalog-count">
        <Sparkles className="w-4 h-4 text-[#C9A84C]" />
        {items === null
          ? "…"
          : visibleCount === count
            ? `${count} design${count === 1 ? "" : "s"} in catalog`
            : `${visibleCount} of ${count} design${count === 1 ? "" : "s"} shown`}
      </div>

      {/* Filters */}
      <div className="luxe-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Product Code or Category…" className="pl-9" data-testid="dist-catalog-search" />
        </div>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="dist-catalog-cat"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36" data-testid="dist-catalog-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="NONE">Available</SelectItem>
            <SelectItem value="MEMO">Memo</SelectItem>
            <SelectItem value="SOLD">Sold</SelectItem>
          </SelectContent>
        </Select>
        {canEdit && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <CatalogFilterBar
              filters={filters}
              onChange={setFilters}
              bounds={bounds}
              variant="admin"
            />
            <Button variant="outline" onClick={selectAll} disabled={!visible || visible.length === 0} data-testid="dist-catalog-select-all">
              {(() => {
                const vCount = visible?.length || 0;
                const picked = (visible || []).filter((p) => selected.has(p.product_code)).length;
                const allPicked = vCount > 0 && picked === vCount;
                return allPicked ? `Clear all (${vCount})` : `Select all (${vCount})`;
              })()}
            </Button>
            <Button onClick={removeSelected} disabled={selected.size === 0 || busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="dist-catalog-bulk-remove">
              <Trash2 className="w-4 h-4 mr-1" /> Delete selected ({selected.size})
            </Button>
          </div>
        )}
        {!canEdit && (
          <div className="ml-auto">
            <CatalogFilterBar
              filters={filters}
              onChange={setFilters}
              bounds={bounds}
              variant="admin"
            />
          </div>
        )}
      </div>

      {/* List */}
      {items === null ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-64 rounded-md" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="luxe-card p-12 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">No catalog products yet.</p>
          <p className="text-slate-400 text-xs mt-2">
            <Link to="/distribution/catalog-upload" className="underline">Upload via Excel</Link> or click <strong>Add product</strong> to create one.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <div className="luxe-card p-12 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">No designs match the current filters.</p>
          <p className="text-slate-400 text-xs mt-2">Try clearing the Gold Wt / Diamond Wt ranges.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {visible.map((p) => {
            const isSel = selected.has(p.product_code);
            const status = (p.status || "NONE").toUpperCase();
            return (
              <div
                key={p.product_code}
                className={`relative luxe-card overflow-hidden transition-all ${isSel ? "ring-2 ring-[#C9A84C] border-[#C9A84C]" : ""}`}
                data-testid={`cat-card-${p.product_code}`}
              >
                {canEdit && (
                  <div className="absolute top-2 left-2 z-10 bg-white/90 rounded p-0.5">
                    <Checkbox checked={isSel} onCheckedChange={() => toggle(p.product_code)} data-testid={`cat-select-${p.product_code}`} />
                  </div>
                )}
                <button
                  onClick={() => cycleStatus(p.product_code, status)}
                  disabled={!canEdit}
                  className={`absolute top-2 right-2 z-10 text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-widest font-semibold ${STATUS_TONE[status]}`}
                  title={canEdit ? "Click to cycle status (None → Memo → Sold)" : ""}
                  data-testid={`cat-status-${p.product_code}`}
                >
                  {status === "NONE" ? "Available" : status}
                </button>
                <div className="aspect-square bg-white flex items-center justify-center p-3">
                  {p.main_photo ? (
                    <img src={p.main_photo} alt={p.product_code} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="w-2/3 h-2/3 rounded bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center text-[10px] text-slate-400 tracking-widest">
                      NO IMAGE
                    </div>
                  )}
                </div>
                <div className="p-3 space-y-1.5 border-t border-[#0D1B2A]/5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-serif text-base text-[#0D1B2A]" data-testid={`cat-code-${p.product_code}`}>{p.product_code}</p>
                    <AshthLiteBadge product={p} size="xs" />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {p.category && <span className="text-[9px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-600 uppercase tracking-wider">{p.category}</span>}
                    <span className="text-[9px] px-1.5 py-0.5 rounded border border-[#b8893b]/40 text-[#b8893b] uppercase tracking-wider">{p.base_purity?.toLowerCase()}</span>
                    {(p.extra_photos?.length || 0) > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500">+{p.extra_photos.length} photo{p.extra_photos.length > 1 ? "s" : ""}</span>}
                  </div>
                  <p className="text-xs text-slate-500 tabular-nums">
                    Gold {formatWeight(p.net_wt || 0)} g · Dia {formatIndian(p.diamond_wt || 0, { decimals: 2 })} ct
                  </p>
                  {canEdit && (
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="outline" onClick={() => setEditing(p.product_code)} className="flex-1 h-7 text-xs" data-testid={`cat-edit-${p.product_code}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => removeOne(p.product_code)} className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid={`cat-delete-${p.product_code}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Forms */}
      <CatalogProductFormDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSaved={() => { setShowCreate(false); load(); }}
      />
      <CatalogProductFormDialog
        open={!!editing}
        productCode={editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}
